import {
  PackageProtectionPricingStrategy,
  type PricingRuleSet,
} from "@redotech/redo-return-coverage-common/coverage-types";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  DEFAULT_PRICING_PERCENTAGE,
  PricingRuleSetSchema,
  pricingRuleSetsFromSettings,
} from "./pricing-rule-set";

export const DEFAULT_MIN_PRICE = 1;

/** The default 2% bracket applied to the $5000 default cart cap. */
export const DEFAULT_MAX_PRICE = 100;

const PricePerItemSchema = z.object({
  basePrice: z.number(),
  pricePerAdditionalItem: z.number(),
  maxPrice: z.number(),
});

/** Money is numbers here; the merchant app adapts its text inputs either way. */
export const PackageProtectionPricingSchema = z.object({
  packageProtectionPlusEnabled: z.boolean(),
  pricingStrategy: createEnum(PackageProtectionPricingStrategy).nullable(),
  minPrice: z.number(),
  maxPrice: z.number(),
  percentage: z.number(),
  minCoverageEligibleCartTotal: z.number().nullable(),
  maxCoverageEligibleCartTotal: z.number().nullable(),
  fixedSubscriptionPrice: z.number().nullable(),
  pricingRuleSet: z.array(PricingRuleSetSchema),
  pricePerItem: PricePerItemSchema.nullable(),
});

/** A merchant edit, bounded like the Mongoose schema, which `updateOne` skips. */
export const PackageProtectionPricingChangesSchema = z
  .object({
    pricingStrategy: PackageProtectionPricingSchema.shape.pricingStrategy,
    minPrice: z.number().min(0.01),
    maxPrice: z.number().min(0.01),
    percentage: z.number().min(0).max(1),
    minCoverageEligibleCartTotal: z.number().min(0).nullable(),
    maxCoverageEligibleCartTotal: z.number().positive().nullable(),
    fixedSubscriptionPrice: z.number().min(0).nullable(),
    pricingRuleSet: z.array(
      PricingRuleSetSchema.extend({
        priceBrackets: z.array(
          z.object({ value: z.number().min(0.001), pricePoint: z.number() }),
        ),
      }),
    ),
    pricePerItem: z
      .object({
        basePrice: z.number().min(0),
        pricePerAdditionalItem: z.number().min(0),
        maxPrice: z.number().min(0.01),
      })
      .nullable(),
  })
  .partial();

export type PackageProtectionPricingChanges = z.infer<
  typeof PackageProtectionPricingChangesSchema
>;

export type PackageProtectionPricing = z.infer<
  typeof PackageProtectionPricingSchema
>;

/** What the read needs, tolerant of the nulls the raw team document carries. */
export interface PackageProtectionPricingSource {
  packageProtectionPlusEnabled?: boolean | null;
  pricingStrategy?: PackageProtectionPricingStrategy | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  percentage?: number | null;
  minCoverageEligibleCartTotal?: number | null;
  maxCoverageEligibleCartTotal?: number | null;
  fixedSubscriptionPrice?: number | null;
  pricingRuleSet?: PricingRuleSet[] | null;
  pricePerItem?: z.infer<typeof PricePerItemSchema> | null;
}

export function packageProtectionPricingFromSettings({
  packageProtection,
  countriesPackageProtection,
}: {
  packageProtection: PackageProtectionPricingSource | null | undefined;
  countriesPackageProtection: string[];
}): PackageProtectionPricing {
  return {
    packageProtectionPlusEnabled:
      packageProtection?.packageProtectionPlusEnabled ?? false,
    pricingStrategy:
      packageProtection?.pricingStrategy ??
      PackageProtectionPricingStrategy.CART_VALUE,
    minPrice: packageProtection?.minPrice ?? DEFAULT_MIN_PRICE,
    maxPrice: packageProtection?.maxPrice ?? DEFAULT_MAX_PRICE,
    percentage: packageProtection?.percentage ?? DEFAULT_PRICING_PERCENTAGE,
    minCoverageEligibleCartTotal:
      packageProtection?.minCoverageEligibleCartTotal ?? null,
    maxCoverageEligibleCartTotal:
      packageProtection?.maxCoverageEligibleCartTotal ?? null,
    fixedSubscriptionPrice: packageProtection?.fixedSubscriptionPrice ?? null,
    pricingRuleSet: pricingRuleSetsFromSettings({
      pricingRuleSet: packageProtection?.pricingRuleSet,
      percentage: packageProtection?.percentage,
      countries: countriesPackageProtection,
    }),
    pricePerItem: packageProtection?.pricePerItem
      ? {
          basePrice: packageProtection.pricePerItem.basePrice,
          pricePerAdditionalItem:
            packageProtection.pricePerItem.pricePerAdditionalItem,
          maxPrice: packageProtection.pricePerItem.maxPrice,
        }
      : null,
  };
}

const NULLABLE_PRICING_PATHS = [
  "pricingStrategy",
  "minCoverageEligibleCartTotal",
  "maxCoverageEligibleCartTotal",
  "fixedSubscriptionPrice",
  "pricePerItem",
] as const;

/** Dotted paths under `team.settings`; a cleared optional field is unset, not stored as null. */
export function packageProtectionPricingTeamPatch(
  pricing: PackageProtectionPricing,
): { set: Record<string, unknown>; unset: string[] } {
  const set: Record<string, unknown> = {
    "packageProtection.minPrice": pricing.minPrice,
    "packageProtection.maxPrice": pricing.maxPrice,
    "packageProtection.percentage": pricing.percentage,
    "packageProtection.pricingRuleSet": pricing.pricingRuleSet,
  };
  const unset: string[] = [];
  for (const field of NULLABLE_PRICING_PATHS) {
    const value = pricing[field];
    if (value === null) {
      unset.push(`packageProtection.${field}`);
    } else {
      set[`packageProtection.${field}`] = value;
    }
  }
  return { set, unset };
}
